import React from 'react';
import {
  Button, EmptyState, ErrorState, KpiCard, Pager, SectionCard, Select, SkeletonText, TextInput,
} from '@parsina/framework';
import { C } from '@parsina/framework/tokens';
import {
  Activity, Clock, Coins, DollarSign, Layers, Percent, SearchX, Stethoscope, TrendingUp, X,
} from 'lucide-react';

import { useTranslation } from '../../i18n';
import { errorMessage, gql } from '../../lib/api';
import { EMPTY, formatUsd } from '../../lib/format';
import { SEARCH_DEBOUNCE_MS } from '../../constants/list';
import {
  LEDGER_PAGE_SIZE, MINUTE_BUCKETS, OWNER_TYPES, num, pageCountOf, visitSortArg, visitsFilter,
  visitsIsFiltered,
} from '../../lib/aiPricingView';
import { VISITS_QUERY, VISIT_ECONOMICS_QUERY, VISIT_OPTIONS_QUERY } from './queries';
import VisitFeaturesTable from './VisitFeaturesTable';
import VisitMinutesTable from './VisitMinutesTable';
import VisitsTable from './VisitsTable';
import VisitDrawer from './VisitDrawer';
import InvocationDrawer from './InvocationDrawer';
import {
  BlockSkeleton, CsvExportButton, TableSkeleton, useEnumLabel, useServiceLabel,
} from './shared';
import { RateLine, useVisitMoney } from './VisitCells';

/**
 * View «ویزیت‌ها» — what a visit's AI EARNED against what it COST (PM-0427).
 *
 * Archetype A strip over an Archetype B list: the filters, six KPIs, the
 * per-feature table (with the runs no visit could claim), the billed-minutes
 * table, then the visits themselves — server-filtered, server-sorted,
 * server-paged — and a drawer per visit.
 *
 * Two requests, two regions: `aiVisitEconomics` feeds the strip and both
 * tables, `aiVisits` the list, so one failing never blanks the other
 * (DESIGN_CONCEPT §2.4). Both are asked at the rate of the policy the
 * «به نرخ سیاست» picker names (`?rate=`, never `?policy=`, which opens the
 * editor) — a DRAFT included, so an operator can read the visits at
 * tomorrow's dollar without activating anything.
 */
export default function VisitsView({ window, view, go }) {
  const { t, formatNumber } = useTranslation();
  const label = useServiceLabel();
  const ownerLabel = useEnumLabel('aiPricing.overview.owner');
  const policyStatus = useEnumLabel('aiPricing.policies.status');

  // ── option lists (a convenience: the regions report their own failures) ──
  const [options, setOptions] = React.useState({ services: [], policies: [] });
  React.useEffect(() => {
    let alive = true;
    gql(VISIT_OPTIONS_QUERY, { window })
      .then((d) => {
        if (!alive) return;
        setOptions({ services: d?.aiServices?.services || [], policies: d?.aiPricingPolicies || [] });
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [window]);

  const filter = React.useMemo(
    () => visitsFilter(view),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [view.owner, view.ownerId, view.service, view.rec, view.mins],
  );
  const policyId = view.rate || null;
  const page = Math.max(1, Number(view.page) || 1);
  const sort = visitSortArg(view);

  // ── summary: aiVisitEconomics ─────────────────────────────────────────────
  const [summary, setSummary] = React.useState(null);
  const [summaryLoading, setSummaryLoading] = React.useState(true);
  const [summaryRefreshing, setSummaryRefreshing] = React.useState(false);
  const [summaryError, setSummaryError] = React.useState(null);

  const loadSummary = React.useCallback(({ quiet = false } = {}) => {
    if (quiet) setSummaryRefreshing(true); else setSummaryLoading(true);
    setSummaryError(null);
    gql(VISIT_ECONOMICS_QUERY, { window, filter, policyId })
      .then((d) => setSummary(d?.aiVisitEconomics || null))
      .catch((e) => setSummaryError(errorMessage(e, t)))
      .finally(() => { setSummaryLoading(false); setSummaryRefreshing(false); });
  }, [window, filter, policyId, t]);

  const firstSummary = React.useRef(true);
  React.useEffect(() => {
    loadSummary({ quiet: !firstSummary.current });
    firstSummary.current = false;
  }, [loadSummary]);

  // ── list: aiVisits ────────────────────────────────────────────────────────
  const [list, setList] = React.useState(null);
  const [listLoading, setListLoading] = React.useState(true);
  const [listRefreshing, setListRefreshing] = React.useState(false);
  const [listError, setListError] = React.useState(null);

  const loadList = React.useCallback(({ quiet = false } = {}) => {
    if (quiet) setListRefreshing(true); else setListLoading(true);
    setListError(null);
    gql(VISITS_QUERY, { window, filter, policyId, page, pageSize: LEDGER_PAGE_SIZE, sort })
      .then((d) => setList(d?.aiVisits || { visits: [], total: 0, page, pageSize: LEDGER_PAGE_SIZE }))
      .catch((e) => setListError(errorMessage(e, t)))
      .finally(() => { setListLoading(false); setListRefreshing(false); });
  }, [window, filter, policyId, page, sort, t]);

  const firstList = React.useRef(true);
  React.useEffect(() => {
    loadList({ quiet: !firstList.current });
    firstList.current = false;
  }, [loadList]);

  // The rate either answer names; the summary's when both have one.
  const money = summary?.money || list?.money || null;
  const conv = useVisitMoney(money);
  const listConv = useVisitMoneyFallback(list?.money, conv);

  // ── the owner-id field echoes keystrokes; the URL follows after a pause ──
  const [ownerDraft, setOwnerDraft] = React.useState(view.ownerId || '');
  React.useEffect(() => { setOwnerDraft(view.ownerId || ''); }, [view.ownerId]);
  React.useEffect(() => {
    if (ownerDraft === (view.ownerId || '')) return undefined;
    const timer = setTimeout(() => go({ ownerId: ownerDraft, page: 1 }), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [ownerDraft, view.ownerId, go]);

  const setFilter = (patch) => go({ ...patch, page: 1 });
  const isFiltered = visitsIsFiltered(view);
  const clear = () => { setOwnerDraft(''); go({ owner: '', ownerId: '', service: '', rec: '', mins: '', page: 1 }); };

  const onSort = (key) => {
    const current = view.sort || 'started';
    const dir = view.dir || 'desc';
    if (current !== key) go({ sort: key, dir: 'desc', page: 1 });
    else if (dir === 'desc') go({ dir: 'asc', page: 1 });
    else go({ sort: '', dir: '', page: 1 });
  };

  const labelOf = React.useCallback((key) => {
    const row = options.services.find((s) => s.serviceKey === key);
    return row ? label(row) : key;
  }, [options.services, label]);

  const all = { value: '', label: t('aiPricing.ledger.filters.all') };
  const bucketOption = (b) => {
    if (b === 'none') return t('aiPricing.visits.minutes.none');
    if (b.endsWith('+')) return t('aiPricing.visits.minutes.atLeast', { min: formatNumber(Number(b.slice(0, -1))) });
    const [lo, hi] = b.split('-');
    return hi
      ? t('aiPricing.visits.minutes.range', { min: formatNumber(Number(lo)), max: formatNumber(Number(hi)) })
      : t('aiPricing.visits.minutes.exact', { count: Number(lo), value: formatNumber(Number(lo)) });
  };

  const rows = list?.visits || [];
  const csvColumns = [
    { key: 'appointmentId', header: t('aiPricing.visits.table.columns.appointment'), value: (r) => r.appointmentId },
    { key: 'firstAt', header: t('aiPricing.visits.drawer.first'), value: (r) => r.firstAt },
    { key: 'lastAt', header: t('aiPricing.visits.drawer.last'), value: (r) => r.lastAt },
    { key: 'ownerType', header: t('aiPricing.ledger.filters.ownerType'), value: (r) => r.ownerType },
    { key: 'billedMinutes', header: t('aiPricing.ledger.drawer.billedMinutes'), value: (r) => r.billedMinutes },
    { key: 'recordings', header: t('aiPricing.visits.drawer.recordings'), value: (r) => r.recordings },
    { key: 'runs', header: t('aiPricing.visits.table.columns.runs'), value: (r) => r.runs },
    { key: 'chargedRuns', header: t('aiPricing.visits.csv.chargedRuns'), value: (r) => r.chargedRuns },
    { key: 'earnedParseh', header: t('aiPricing.visits.csv.earnedParseh'), value: (r) => r.earnedParseh },
    { key: 'earnedUsd', header: t('aiPricing.visits.drawer.earnedUsd'), value: (r) => r.earnedUsd },
    { key: 'paidUsd', header: t('aiPricing.visits.csv.paidUsd'), value: (r) => r.paidUsd },
    { key: 'providerPaidUsd', header: t('aiPricing.visits.drawer.providerPaid'), value: (r) => r.providerPaidUsd },
    { key: 'marginPct', header: t('aiPricing.visits.table.columns.margin'), value: (r) => r.marginPct },
    { key: 'unpricedRuns', header: t('aiPricing.visits.drawer.unpriced'), value: (r) => r.unpricedRuns },
  ];

  const stats = summary?.stats || null;
  const noVisits = !!summary && (stats?.visits ?? 0) === 0;
  // No answer yet and no failure is still loading — a quiet refetch clears the
  // error before its answer lands, and that gap must not render "content".
  const summaryPending = summaryLoading || (!summary && !summaryError);
  const summaryState = summaryPending ? 'loading' : summaryError ? 'error' : noVisits ? 'empty' : 'content';

  const emptyVisits = (
    <EmptyState
      icon={<Activity size={44} strokeWidth={1.5} aria-hidden />}
      title={t('aiPricing.visits.empty.title')}
      description={t('aiPricing.visits.empty.desc')}
      data-testid="ai-visits-empty"
    />
  );
  const emptyFiltered = (
    <EmptyState
      icon={<SearchX size={44} strokeWidth={1.5} aria-hidden />}
      title={t('aiPricing.visits.emptyFiltered.title')}
      description={t('aiPricing.visits.emptyFiltered.desc')}
      action={<Button variant="outline" onClick={clear}>{t('common.actions.clearFilters')}</Button>}
      data-testid="ai-visits-empty"
    />
  );

  return (
    <div data-testid="ai-visits">
      {/* ④ Filters — every one in the URL, applied at once. */}
      <div
        className="mb-2 grid grid-cols-2 gap-3 rounded-lg border border-border bg-card p-3 md:grid-cols-3 xl:grid-cols-6"
        role="group"
        aria-label={t('aiPricing.visits.filters.label')}
        data-testid="ai-visits-filters"
      >
        <Select
          label={t('aiPricing.ledger.filters.ownerType')}
          value={view.owner || ''}
          onChange={(e) => setFilter({ owner: e.target?.value ?? e })}
          options={[all, ...OWNER_TYPES.map((o) => ({ value: o, label: ownerLabel(o) }))]}
          placeholder={t('aiPricing.ledger.filters.all')}
          data-testid="ai-visits-filter-owner"
        />
        <TextInput
          label={t('aiPricing.ledger.filters.ownerId')}
          value={ownerDraft}
          onChange={(e) => setOwnerDraft(e.target?.value ?? e)}
          dir="ltr"
          inputStyle={{ fontFamily: 'monospace' }}
          data-testid="ai-visits-filter-owner-id"
        />
        <Select
          label={t('aiPricing.visits.filters.recorded')}
          value={view.rec || ''}
          onChange={(e) => setFilter({ rec: e.target?.value ?? e })}
          options={[
            all,
            { value: 'yes', label: t('aiPricing.visits.filters.recordedYes') },
            { value: 'no', label: t('aiPricing.visits.filters.recordedNo') },
          ]}
          placeholder={t('aiPricing.ledger.filters.all')}
          data-testid="ai-visits-filter-recorded"
        />
        <Select
          label={t('aiPricing.visits.filters.minutes')}
          value={view.mins || ''}
          onChange={(e) => setFilter({ mins: e.target?.value ?? e })}
          options={[all, ...MINUTE_BUCKETS.map((b) => ({ value: b, label: bucketOption(b) }))]}
          placeholder={t('aiPricing.ledger.filters.all')}
          data-testid="ai-visits-filter-minutes"
        />
        <Select
          label={t('aiPricing.visits.filters.service')}
          value={view.service || ''}
          onChange={(e) => setFilter({ service: e.target?.value ?? e })}
          options={[all, ...options.services.map((s) => ({ value: s.serviceKey, label: label(s) }))]}
          placeholder={t('aiPricing.ledger.filters.all')}
          data-testid="ai-visits-filter-service"
        />
        <Select
          label={t('aiPricing.visits.filters.rate')}
          value={view.rate || ''}
          // The rate is not a filter: it changes no visit, only the toman figures.
          onChange={(e) => go({ rate: e.target?.value ?? e })}
          options={[
            { value: '', label: t('aiPricing.visits.filters.rateActive') },
            ...options.policies.map((p) => ({
              value: String(p.id),
              label: t('aiPricing.visits.filters.rateOption', { name: p.name, status: policyStatus(p.status) }),
            })),
          ]}
          placeholder={t('aiPricing.visits.filters.rateActive')}
          data-testid="ai-visits-filter-rate"
        />
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <p className="flex flex-wrap items-center gap-x-3 text-xs text-muted-foreground" aria-live="polite" data-testid="ai-visits-count">
          {list && !listLoading && !listError ? <span>{t('aiPricing.visits.count', { count: list.total || 0 })}</span> : null}
          {money ? <RateLine money={money} conv={conv} data-testid="ai-visits-rate" /> : null}
        </p>
        {isFiltered ? (
          <Button variant="ghost" size="sm" onClick={clear} data-testid="ai-visits-clear">
            <X size={14} aria-hidden />
            {t('common.actions.clearFilters')}
          </Button>
        ) : null}
        <div className="ms-auto">
          <CsvExportButton columns={csvColumns} rows={rows} data-testid="ai-visits-csv" />
        </div>
      </div>

      {/* ③ + the two summary tables — one request, one region. */}
      <div
        className="transition-opacity"
        style={{ opacity: summaryRefreshing ? 0.45 : 1 }}
        aria-busy={summaryRefreshing || undefined}
        data-testid="ai-visits-summary"
        data-state={summaryState}
      >
        {summaryPending ? (
          <SummarySkeleton label={t('aiPricing.visits.loading')} />
        ) : summaryError ? (
          // Money is hidden on error: no strip of zeros beside a failure.
          <ErrorState
            title={t('aiPricing.visits.summaryError')}
            description={summaryError}
            onRetry={loadSummary}
            retryLabel={t('common.actions.retry')}
            data-testid="ai-visits-summary-error"
          />
        ) : (
          <>
            <VisitKpis stats={stats} conv={conv} />
            <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
              <SectionCard
                title={t('aiPricing.visits.features.title')}
                sub={t('aiPricing.visits.features.sub')}
                icon={Layers}
                data-testid="ai-visits-features"
                data-state={noVisits || !(summary.groups || []).length ? 'empty' : 'content'}
              >
                {noVisits || !(summary.groups || []).length ? (
                  <EmptyState
                    icon={<Activity size={44} strokeWidth={1.5} aria-hidden />}
                    title={isFiltered ? t('aiPricing.visits.emptyFiltered.title') : t('aiPricing.visits.empty.title')}
                    description={isFiltered ? t('aiPricing.visits.emptyFiltered.desc') : t('aiPricing.visits.empty.desc')}
                    data-testid="ai-visits-features-empty"
                  />
                ) : (
                  <div className="overflow-x-auto">
                    <VisitFeaturesTable groups={summary.groups} stats={stats} conv={conv} />
                  </div>
                )}
                <Unattributed rows={summary.unattributed || []} conv={conv} />
              </SectionCard>
              <SectionCard
                title={t('aiPricing.visits.minutes.title')}
                sub={t('aiPricing.visits.minutes.sub')}
                icon={Clock}
                data-testid="ai-visits-minutes"
                data-state={noVisits ? 'empty' : 'content'}
              >
                {noVisits ? (
                  <EmptyState
                    icon={<Activity size={44} strokeWidth={1.5} aria-hidden />}
                    title={isFiltered ? t('aiPricing.visits.emptyFiltered.title') : t('aiPricing.visits.empty.title')}
                    description={t('aiPricing.visits.minutes.emptyDesc')}
                    data-testid="ai-visits-minutes-empty"
                  />
                ) : (
                  <div className="overflow-x-auto">
                    <VisitMinutesTable
                      buckets={summary.byMinutes || []}
                      conv={conv}
                      active={view.mins || ''}
                      onPick={(mins) => setFilter({ mins })}
                    />
                  </div>
                )}
              </SectionCard>
            </div>
          </>
        )}
      </div>

      {/* ⑤ The visits. */}
      <h2 className="mb-2 mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-foreground">
        <Stethoscope size={16} aria-hidden />
        {t('aiPricing.visits.table.title')}
      </h2>
      <div
        className="rounded-lg border border-border bg-card transition-opacity"
        style={{ opacity: listRefreshing ? 0.45 : 1 }}
        aria-busy={listRefreshing || undefined}
        data-testid="ai-visits-list"
        data-state={listLoading ? 'loading' : listError ? 'error' : rows.length ? 'content' : 'empty'}
      >
        {listLoading ? (
          <TableSkeleton cols={9} label={t('aiPricing.visits.table.loading')} data-testid="ai-visits-loading" />
        ) : listError ? (
          <ErrorState
            title={t('aiPricing.visits.table.error')}
            description={listError}
            onRetry={loadList}
            retryLabel={t('common.actions.retry')}
            data-testid="ai-visits-error"
          />
        ) : (
          <VisitsTable
            rows={rows}
            conv={listConv}
            view={view}
            onSort={onSort}
            onOpen={(r) => go({ visit: r.appointmentId, inv: '' })}
            empty={isFiltered ? emptyFiltered : emptyVisits}
          />
        )}
      </div>

      {!listLoading && !listError ? (
        <div data-testid="ai-visits-pager">
          <PagerFor list={list} page={page} go={go} />
        </div>
      ) : null}

      {view.visit ? (
        <VisitDrawer
          key={view.visit}
          appointmentId={view.visit}
          policyId={policyId}
          inv={view.inv}
          labelOf={labelOf}
          onOpenInvocation={(requestId) => go({ inv: requestId })}
          onBack={() => go({ inv: '' })}
          onClose={() => go({ visit: '', inv: '' })}
        />
      ) : view.inv ? (
        <InvocationDrawer requestId={view.inv} labelOf={labelOf} onClose={() => go({ inv: '' })} />
      ) : null}
    </div>
  );
}

/**
 * The list converts at the rate ITS answer names; until a list has answered,
 * the summary's. They are the same policy unless one request raced a change
 * of the picker.
 */
function useVisitMoneyFallback(money, fallback) {
  const own = useVisitMoney(money);
  return money ? own : fallback;
}

function PagerFor({ list, page, go }) {
  const { t, formatNumber } = useTranslation();
  return (
    <Pager
      page={page}
      pageCount={pageCountOf(list?.total)}
      pageSize={LEDGER_PAGE_SIZE}
      onChange={(next) => go({ page: next })}
      prevLabel={t('common.pager.prev')}
      nextLabel={t('common.pager.next')}
      pageLabel={(p, total) => t('common.pager.label', { page: p, total })}
      formatNumber={formatNumber}
    />
  );
}

/**
 * ③ Six KPIs. A figure the service could not compute is «—»; the visit count
 * is a real count, so zero visits is «۰», not a dash.
 */
function VisitKpis({ stats, conv }) {
  const { t, formatNumber, formatPercent } = useTranslation();
  const s = stats || {};
  const toman = (v) => (num(v) == null ? EMPTY : formatNumber(num(v), { maximumFractionDigits: 0 }));
  const parseh = (v) => (num(v) == null ? EMPTY : formatNumber(num(v), { maximumFractionDigits: 2 }));
  const usd = (v) => formatUsd(v, { formatNumber, t });
  const tomanUnit = t('common.units.toman');
  const parsehUnit = t('common.units.parseh');
  const usdUnit = t('common.units.usd');

  // Earned is پارسه, paid is USD; where the credit is sold in toman both read
  // toman first, at the policy's rate, with the native figure underneath.
  const earned = (v) => (conv.irt
    ? { value: toman(conv.fromParseh(v)), unit: tomanUnit, native: `${parseh(v)} ${parsehUnit}` }
    : { value: parseh(v), unit: parsehUnit, native: null });
  const paid = (v) => (conv.irt
    ? { value: toman(conv.fromUsd(v)), unit: tomanUnit, native: `${usd(v)} ${usdUnit}` }
    : { value: usd(v), unit: usdUnit, native: null });
  // A percentile tile reads the visit's EARNED figure, its PAID one beneath.
  const paidLine = (p) => [t('aiPricing.visits.kpi.paidLine', { value: p.value === EMPTY ? EMPTY : `${p.value} ${p.unit}` })];

  const meanEarned = earned(s.meanEarnedParseh);
  const meanPaid = paid(s.meanPaidUsd);
  const p50e = earned(s.p50EarnedParseh);
  const p50p = paid(s.p50PaidUsd);
  const p90e = earned(s.p90EarnedParseh);
  const p90p = paid(s.p90PaidUsd);
  const negative = num(s.marginPct) != null && num(s.marginPct) < 0;

  const kpis = [
    {
      key: 'visits',
      label: t('aiPricing.visits.kpi.visits'),
      sub: [t('aiPricing.visits.kpi.recorded', { count: s.recordedVisits ?? 0 })],
      value: formatNumber(s.visits ?? 0),
      icon: <Stethoscope size={20} color={C.navy} />,
      bg: C.navyLight,
    },
    {
      key: 'earned',
      label: t('aiPricing.visits.kpi.meanEarned'),
      sub: meanEarned.native ? [meanEarned.native] : [],
      value: meanEarned.value,
      unit: meanEarned.unit,
      icon: <Coins size={20} color={C.green} />,
      bg: C.greenLight,
    },
    {
      key: 'paid',
      label: t('aiPricing.visits.kpi.meanPaid'),
      sub: [
        ...(meanPaid.native ? [meanPaid.native] : []),
        ...(s.meanProviderPaidUsd != null ? [t('aiPricing.visits.kpi.provider', { value: usd(s.meanProviderPaidUsd) })] : []),
      ],
      value: meanPaid.value,
      unit: meanPaid.unit,
      icon: <DollarSign size={20} color={C.teal} />,
      bg: C.tealLight,
    },
    {
      key: 'margin',
      label: t('aiPricing.visits.kpi.margin'),
      sub: num(s.pricedShare) == null ? [] : [t('aiPricing.visits.kpi.pricedShare', {
        share: formatNumber(num(s.pricedShare), { style: 'percent', maximumFractionDigits: 0 }),
      })],
      value: num(s.marginPct) == null ? EMPTY : formatPercent(num(s.marginPct)),
      icon: <Percent size={20} color={negative ? C.red : C.green} />,
      bg: negative ? C.redLight : C.greenLight,
    },
    {
      key: 'median',
      label: t('aiPricing.visits.kpi.median'),
      sub: paidLine(p50p),
      value: p50e.value,
      unit: p50e.unit,
      icon: <TrendingUp size={20} color={C.amber} />,
      bg: C.amberLight,
    },
    {
      key: 'p90',
      label: t('aiPricing.visits.kpi.p90'),
      sub: paidLine(p90p),
      value: p90e.value,
      unit: p90e.unit,
      icon: <TrendingUp size={20} color={C.red} />,
      bg: C.redLight,
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-3.5 md:grid-cols-3 xl:grid-cols-6" data-testid="ai-visits-kpis">
      {kpis.map((kpi) => (
        <div key={kpi.key} data-testid={`ai-visits-kpi-${kpi.key}`}>
          <KpiCard
            minWidth={0}
            label={(
              <span className="block">
                <span className="block">{kpi.label}</span>
                {kpi.sub.map((line) => (
                  <span key={line} className="mt-0.5 block text-[10.5px] text-muted-foreground">{line}</span>
                ))}
              </span>
            )}
            value={kpi.value}
            unit={kpi.value === EMPTY ? undefined : kpi.unit}
            icon={kpi.icon}
            bg={kpi.bg}
          />
        </div>
      ))}
    </div>
  );
}

/**
 * The runs that no visit could claim (PM-0432): a patient id or a file id
 * where an appointment id belongs, or no id at all. Shown as a note under the
 * feature table — with a per-visit ESTIMATE, labelled as one — so «نکات
 * بالینی» stays visible instead of silently missing from the visit's cost.
 */
function Unattributed({ rows, conv }) {
  const { t, formatNumber } = useTranslation();
  const label = useServiceLabel();
  if (!rows.length) return null;
  const toman = (v) => (num(v) == null ? EMPTY : formatNumber(num(v), { maximumFractionDigits: 0 }));
  const parseh = (v) => (num(v) == null ? EMPTY : formatNumber(num(v), { maximumFractionDigits: 2 }));
  const estimate = (r) => (conv.irt
    ? t('aiPricing.visits.unattributed.estimateToman', {
      earned: toman(conv.fromParseh(r.perVisitEarnedParseh)),
      paid: toman(conv.fromUsd(r.perVisitPaidUsd)),
    })
    : t('aiPricing.visits.unattributed.estimateNative', {
      earned: parseh(r.perVisitEarnedParseh),
      paid: formatUsd(r.perVisitPaidUsd, { formatNumber, t }),
    }));
  return (
    <div className="mt-4 rounded-lg border border-border bg-muted px-4 py-3 text-xs text-muted-foreground" role="note" data-testid="ai-visits-unattributed">
      <p className="mb-1.5 font-semibold text-foreground">{t('aiPricing.visits.unattributed.title')}</p>
      <ul className="flex flex-col gap-1">
        {rows.map((r) => (
          <li key={r.serviceKey} data-testid={`ai-visits-unattributed-${r.serviceKey}`}>
            {t('aiPricing.visits.unattributed.row', {
              service: label(r),
              runs: formatNumber(r.runs ?? 0),
              withoutId: formatNumber(r.withoutIdRuns ?? 0),
            })}
            {' '}
            <span className="whitespace-nowrap">{estimate(r)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Six KPI blocks and two table cards, in the real grid — nothing jumps on arrival. */
function SummarySkeleton({ label }) {
  return (
    <div className="space-y-5" data-testid="ai-visits-summary-loading" data-state="loading" role="status" aria-busy="true" aria-label={label}>
      <div className="grid grid-cols-2 gap-3.5 md:grid-cols-3 xl:grid-cols-6">
        {Array.from({ length: 6 }).map((_, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} className="rounded-xl border border-border bg-card p-4">
            <SkeletonText width="60%" height={22} />
            <div className="mt-3"><SkeletonText width="80%" height={11} /></div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <div className="rounded-xl border border-border bg-card p-5"><BlockSkeleton lines={7} height={16} /></div>
        <div className="rounded-xl border border-border bg-card p-5"><BlockSkeleton lines={7} height={16} /></div>
      </div>
    </div>
  );
}
